import React, {useEffect, useMemo, useCallback, useRef, useState} from "react"
import {useFormContext} from "react-hook-form";
import Uppy from "@uppy/core";
import Webcam from "@uppy/webcam";
import {DashboardModal} from "@uppy/react";
import {PinturaEditorModal} from "react-pintura";
import {createDefaultImageWriter, getEditorDefaults} from "pintura";
import {useFormFieldAttributes, useOptionValues} from "../../utils";
import classNames from "classnames";
import CustomError from "./custom_error";
import RenderedHtmlContent from "../../../../../shared/virtual_congress/components/rendered_html_content";
import {
  locale_en_gb,
  markup_editor_locale_en_gb,
  plugin_crop_locale_en_gb,
  plugin_annotate_locale_en_gb,
  plugin_decorate_locale_en_gb,
  plugin_filter_locale_en_gb,
  plugin_finetune_locale_en_gb,
  plugin_resize_locale_en_gb,
  plugin_sticker_locale_en_gb,
  plugin_redact_locale_en_gb,
  plugin_frame_locale_en_gb,
} from '@pqina/pintura';

const ALLOWED_FILE_TYPES = ["image/gif", "image/jpeg", "image/png"]
const backendFieldName = 'tag_owner_images'

const UppyModalUploader = ({buttonLabel, formMode, field, pinturaConfig}) => {
  const {clearErrors, setValue} = useFormContext();
  const [open, setOpen] = useState(false)
  const [selectedFileName, setSelectedFileName] = useState()

  const [editorEnabled, setEditorEnabled] = useState(false);
  const [editorSrc, setEditorSrc] = useState(undefined);
  const editorRef = useRef(null)
  const editorConfig = useMemo(() => {
    return getEditorDefaults();
  }, [])

  const handleEditorHide = useCallback(() => {
    setEditorEnabled(false)
  }, [setEditorEnabled]);

  const handleEditorProcess = useCallback((imageState) => {
    clearErrors(backendFieldName)
    setValue(backendFieldName, {"0": imageState.dest})
    setEditorEnabled(false);
  }, [
    backendFieldName,
    clearErrors,
    setValue,
    setEditorEnabled,
  ]);



  const uppy = useMemo(() => {
    if (!editorRef) {
      return null
    }

    return new Uppy({
      restrictions: {
        maxFileSize: 3000000,
        maxNumberOfFiles: 1,
        minNumberOfFiles: 1,
        allowedFileTypes: ALLOWED_FILE_TYPES
      },
      onBeforeFileAdded: (file) => {
        if (file.handledByDoka) {
          setOpen(false)
          clearErrors(backendFieldName)
          setValue(backendFieldName, {"0": file.data})
          return true;
        }

        const fileReader = new FileReader()
        fileReader.readAsDataURL(file.data);
        fileReader.onload = function () {
          setEditorEnabled(true);
          setEditorSrc(this.result);
          setSelectedFileName(file?.data?.name)
        };

        setOpen(false)
        clearErrors(backendFieldName)
        setValue(backendFieldName, {"0": file.data})
        return false;
      },
    })
      .use(Webcam, {
        countdown: false,
        mirror: false,
        showRecordingLength: false,
        preferredVideoMimeType: null,
        preferredImageMimeType: null,
        modes: ['picture'],
        videoConstraints: {
          facingMode: 'environment',
          showVideoSourceDropdown: true,
          width: {min: 720, ideal: 1280, max: 1920},
          height: {min: 480, ideal: 800, max: 1080},
        },
        locale: {},
      })
  }, [])

  useEffect(() => {
    uppy.on('file-removed', (file, reason) => {
      clearErrors(backendFieldName)
      setValue(backendFieldName, {})
    })
    return () => uppy.close()
  }, [])


  const pinturaEditorModal = useMemo(() => {
    const {locale, imageWriter, ...currentPinturaConfig} = pinturaConfig
    const currentEditorConfig = {...editorConfig}
    let currentLocale = undefined

    if (locale) {
      currentPinturaConfig.locale = {
        ...locale_en_gb,
        ...plugin_annotate_locale_en_gb,
        ...plugin_crop_locale_en_gb,
        ...plugin_decorate_locale_en_gb,
        ...plugin_filter_locale_en_gb,
        ...plugin_finetune_locale_en_gb,
        ...plugin_resize_locale_en_gb,
        ...plugin_sticker_locale_en_gb,
        ...plugin_redact_locale_en_gb,
        ...plugin_frame_locale_en_gb,
        ...markup_editor_locale_en_gb,
        ...locale
      }
    }

    const currentImageWriter = createDefaultImageWriter(imageWriter)

    console.log({
      pinturaConfig,
      editorConfig,
      editorSrc,
      currentPinturaConfig,
      imageWriter,
    })

    return <PinturaEditorModal
      {...editorConfig}
      src={editorSrc}
      {...currentPinturaConfig}
      imageWriter={currentImageWriter}
      onHide={handleEditorHide}
      onProcess={handleEditorProcess}
    />
  }, [editorConfig, pinturaConfig, editorSrc, handleEditorHide, handleEditorProcess])

  const handleOnButtonClick = () => {
    uppy.reset()
    setValue(backendFieldName, {})
    setOpen(true)
  }

  return <>
    <button
      type={"button"}
      className="btn btn-light btn-block"
      id="uppy-modal-btn"
      onClick={handleOnButtonClick}
    >
      {buttonLabel}
    </button>

    {selectedFileName && <small className={"uppy-file-name"}>
      {selectedFileName}
    </small>}
    <DashboardModal
      uppy={uppy}
      open={open}
      closeModalOnClickOutside
      onRequestClose={setOpen}
      hideUploadButton={true}
      plugins={['Webcam']}
    />


    {editorEnabled && pinturaEditorModal}
  </>
}


const PinturaImageTag = ({field, formMode, type}) => {
  const {
    labelWidth,
    inputWidth,
    title,
    hint,
    fieldValue,
  } = useFormFieldAttributes(formMode, field)
  const optionValues = useOptionValues(formMode, field)
  const {formState: { errors }} = useFormContext();
  const errorMessage = (errors[backendFieldName])

  const {clearErrors} = useFormContext();
  const [customFileInputLabel, setCustomFileInputLabel] = useState(title)
  const [selectedFileName, setSelectedFileName] = useState()

  const pinturaConfig = useMemo(() => {
    if (fieldValue) {
      try {
        return JSON.parse(fieldValue)
      } catch (e) {
        alert(e)
      }
    }

    return {}
  }, [fieldValue])


  let customFileInputDefaultLabel = title
  optionValues.map(row => {
    const key = row[0]
    const value = row[1]

    if (key === "custom-file-input-default-label") {
      customFileInputDefaultLabel = value
    }
  })

  useEffect(() => {
    setCustomFileInputLabel(customFileInputDefaultLabel)
  }, [])

  let inputContent = <div className="custom-file">
    <input
      type="file"
      accept={ALLOWED_FILE_TYPES.join(", ")}
      className={classNames("custom-file-input form-control-sm", {"is-invalid": errorMessage})}
      id={backendFieldName}
      name={backendFieldName}
      onChange={(e) => {
        //console.log(e.target?.files)
        if (e.target?.files && e.target.files[0].name) {
          setSelectedFileName(e.target.files[0].name)
        } else {
          setSelectedFileName("")
        }
        clearErrors(backendFieldName)
      }}
    />
    <label
      className={classNames("custom-file-label col-form-label-sm text-left", {"is-invalid": errorMessage})}
      htmlFor={backendFieldName}
    >
      {selectedFileName || customFileInputLabel}
    </label>
    <CustomError error={errorMessage}/>
  </div>

  if (type === "tag_owner_image_pintura") {
    let buttonLabel = "Click to select a file."
    if (optionValues) {
      optionValues.map(row => {
        const key = row[0]
        const value = row[1]

        if (key === "button-label") {
          buttonLabel = value
        }
      })
    }

    inputContent = <>
      <div className={classNames({"is-invalid": errorMessage})}>
        <UppyModalUploader
          buttonLabel={buttonLabel}
          formMode={formMode}
          field={field}
          pinturaConfig={pinturaConfig}
        />
        {hint && <small className="form-text text-muted">
          <RenderedHtmlContent html={hint}/>
        </small>}
      </div>
      <CustomError error={errorMessage}/>
    </>
  }

  return <div className="form-group row" id={`form-group-${field}`} key={field}>
    <label htmlFor={backendFieldName} id={field}
           className={`col-md-${labelWidth} r-label col-form-label`}>
      {title || field}
    </label>
    <div className={`col-md-${inputWidth}`}>
      {inputContent}
    </div>
  </div>
}

export default PinturaImageTag
